/**
 * 🔴🔴 AI-MENU-A (@UC0353 · Jira GOPPY-45 · `sdlc/batches/AI-MENU-GOPPY45-20261028.md` Q1–Q8) — CATALOG MENU KHÁM PHÁ: MỘT file quyết
 * trang nào có nút ở lượt menu/chào, nằm ở MỤC nào, đứng thứ mấy, và có phải nút KHỞI ĐẦU (một trong vài nút hiện sẵn) không.
 *
 * Trước lô này lượt menu mang MỌI trang chat được phép theo THỨ TỰ KHAI SỔ — thứ tự mà chính `registry.ts` ghi là *"không phải ưu tiên"*
 * (owner nhóm mới thấy «Tạo nhóm» · «Thêm quỹ cho nhóm» ngay dưới lời chào). Câu hỏi *"ai xứng đáng một chip"* (hướng 2/3 của
 * US-AIV2-0076) nay có câu trả lời THẬT ở đây, nên thứ tự Sổ không còn quyết gì cho menu.
 *
 * Luật của file (khoá ở `menu-catalog.invariants.spec.ts`, TỰ KHÁM PHÁ từ Sổ):
 *  - mỗi trang CHAT (`isAiChatRunnablePage`) có ĐÚNG MỘT dòng: một mục (trực tiếp, hoặc là một KIỂU của một họ gộp) HOẶC ẩn kèm lý do.
 *    Thêm một trang chat mà quên khai ở đây ⇒ test đỏ — không trang nào rơi khỏi menu im lặng, cũng không trang nào tự chen vào menu;
 *  - trang `redirect` (chỉ mở màn hình) mặc định KHÔNG có nút menu; muốn có thì khai dòng (vd `FIX-MISTAKE` — Q5);
 *  - id họ gộp KHÔNG trùng id trang (nút họ là `choose` mang `payload.family`, không phải `run_page`);
 *  - hạng khởi đầu đôi một khác nhau (thứ tự tất định).
 *
 * File là DỮ LIỆU — không đọc DB, không biết vai: lọc theo vai/cờ/nhóm là việc của cổng năng lực ở tầng api (trang không được phép thì không
 * bao giờ tới hàm ghép). Câu chữ nhãn KIỂU của họ gộp là chữ người dùng đọc ⇒ lưới `no-accounting-jargon` quét file này.
 */

/** Khoá mục — chuỗi WIRE ổn định (`aiChipSchema.section`), client map sang ARB. Thứ tự mảng = thứ tự mục trên màn Khám phá. */
export const AI_MENU_SECTIONS = ["group_members", "contributions", "spending_reports"] as const;
export type AiMenuSection = (typeof AI_MENU_SECTIONS)[number];

/** Một KIỂU trong họ gộp: trang chạy thật + nhãn nút của lượt hỏi kiểu (nói theo việc, không theo tên trang). */
export interface AiMenuFamilyKind {
  readonly page: string;
  readonly label: string;
}

/**
 * HỌ GỘP — nhiều trang cùng một VIỆC với người dùng ("tạo đợt thu"), khác nhau ở KIỂU. Menu ra MỘT nút họ; bấm ⇒ server hỏi kiểu
 * (`question`) với một nút `run_page` cho mỗi kiểu ĐƯỢC PHÉP (đi đúng đường nút cũ). Trang trong họ giữ nguyên (ô · binding · eval).
 */
export interface AiMenuFamily {
  readonly id: string;
  readonly label: string;
  readonly question: string;
  readonly kinds: readonly AiMenuFamilyKind[];
}

/** Một dòng của mục: một trang, hoặc một họ gộp. `starter` = hạng nút khởi đầu (nhỏ đứng trước). `situational` = Q4 (chỉ khi `ready()` ≠ `not_ready`). */
export type AiMenuItem =
  | { readonly page: string; readonly starter?: number; readonly situational?: true }
  | { readonly family: AiMenuFamily; readonly starter?: number };

export interface AiMenuSectionDef {
  readonly id: AiMenuSection;
  readonly items: readonly AiMenuItem[];
}

/** Trang chat CỐ Ý không có nút menu (vẫn `live`, gõ vẫn làm được) — lý do bắt buộc, đủ để người sau không "sửa lại cho đủ". */
export interface AiMenuHidden {
  readonly page: string;
  readonly reason: string;
}

export interface AiMenuCatalog {
  readonly sections: readonly AiMenuSectionDef[];
  readonly hidden: readonly AiMenuHidden[];
}

/** Q3 — họ «Tạo đợt thu»: ba trang mở đợt thu khác nhau ở cách thu (một lần · có hạn chót · lặp lại). */
export const AI_MENU_FAMILY_COLLECTION: AiMenuFamily = Object.freeze({
  id: "COL-NEW",
  label: "Tạo đợt thu",
  question: "Bạn muốn thu theo cách nào?",
  kinds: Object.freeze([
    // COL-EQUAL: người mở không nói hạn — binding đặt hạn cuối tháng hiện tại.
    { page: "COL-EQUAL", label: "Thu một lần, hạn cuối tháng" },
    // COL-DEADLINE: người mở nói rõ hạn chót; quá hạn thì khoản chưa đóng có thể nhắc.
    { page: "COL-DEADLINE", label: "Thu một lần, tự chọn hạn" },
    // COL-RECURRING: lịch thu lặp lại, mỗi kỳ cùng một số tiền.
    { page: "COL-RECURRING", label: "Thu lặp lại hằng tháng/quý" },
  ]),
});

/** Q3 — họ «Ghi khoản chi»: hai trang ghi chi khác nhau ở AI TRẢ (quỹ trả · một người trả trước rồi chia đều). */
export const AI_MENU_FAMILY_EXPENSE: AiMenuFamily = Object.freeze({
  id: "EXP-NEW",
  label: "Ghi khoản chi",
  question: "Khoản chi này ai trả?",
  kinds: Object.freeze([
    // EXP-F-NOALLOC: quỹ trả, không chia cho ai, không ai ứng trước.
    { page: "EXP-F-NOALLOC", label: "Quỹ trả" },
    // EXP-F-EQUAL: một người ứng tiền trả trước, chia đều cho các thành viên.
    { page: "EXP-F-EQUAL", label: "Một người trả trước, chia đều" },
  ]),
});

/**
 * Q6–Q8 — bảng catalog (hồ sơ lô). Thứ tự dòng trong mục = thứ tự trên màn Khám phá; `starter` 1..5 = ba nút hiện sẵn (sau nút thẻ) theo vai:
 * owner nhóm mới ⇒ «Tạo đợt thu» · «Xem số dư quỹ» (· «Ghi khoản chi»…); member ⇒ «Xem số dư quỹ» · «Xem thông tin của tôi» · «Xem các đợt thu».
 */
export const AI_MENU_CATALOG: AiMenuCatalog = Object.freeze({
  sections: Object.freeze<AiMenuSectionDef[]>([
    {
      id: "group_members",
      items: [
        { page: "ONB-ADD-MEMBERS" },
        { page: "READ-PEOPLE-MINE", starter: 4 },
        { page: "ONB-CREATE-GROUP" },
        // `redirect` của người CHỜ DUYỆT (closed-set không-nhóm): nút chỉ có khi có lời xin thật — tầng api lọc (C07-STRAY `menuPagesFor`).
        { page: "JOIN-REQUEST-MINE" },
      ],
    },
    {
      id: "contributions",
      items: [
        { family: AI_MENU_FAMILY_COLLECTION, starter: 1 },
        { page: "READ-COLLECTIONS", starter: 5 },
        { page: "PAY-REPORT" },
        { page: "GROUP-BANK-ACCOUNT" },
        { page: "DEBT-REMIND", situational: true },
        { page: "PAY-CONFIRM-DECIDE", situational: true },
        { page: "DUE-CLAIM-WITHDRAW", situational: true },
        { page: "RECORD-PAID" },
        // AI-MENU-POB-ENTRY (@UC0218 · 28/10): chỉ khi người này đóng hộ được ai HOẶC có lời đề nghị chờ trả lời (`ready()` không đối tượng của binding).
        { page: "PAY-ON-BEHALF", situational: true },
      ],
    },
    {
      id: "spending_reports",
      items: [
        { page: "READ-FUND-MONEY", starter: 2 },
        { family: AI_MENU_FAMILY_EXPENSE, starter: 3 },
        { page: "EXP-APPROVE", situational: true },
        // Q5: trang `redirect` (mở màn sửa khoản chi) — vào Khám phá theo VAI (O/A/T), chưa có điều kiện «có khoản sửa được» (nợ ghi ở hồ sơ lô).
        { page: "FIX-MISTAKE" },
        { page: "INC-RECORD" },
        { page: "READ-DEBTS" },
        { page: "REPORT-EXPORT" },
      ],
    },
  ]),
  hidden: Object.freeze<AiMenuHidden[]>([
    {
      page: "ONB-CREATE-FUND",
      reason:
        "Mọi nhóm đã có «Quỹ chung» từ lúc lập — nút gợi ý «Thêm quỹ» làm người dùng tưởng nhóm chưa có quỹ. Trang vẫn chạy khi người dùng gõ muốn tách thêm quỹ.",
    },
    {
      page: "HELP-FROM-REGISTRY",
      reason: "Màn Khám phá trợ lý thay cho nút «Trợ lý làm được gì»; gõ câu hỏi đó vẫn ra lượt menu khám phá (không bung danh sách dài).",
    },
  ]),
});

/** Chỗ của một trang trong catalog: mục + (nếu có) họ gộp chứa nó. `undefined` = trang không có dòng (redirect mặc định) hoặc ẩn. */
export interface AiMenuPlacement {
  readonly section: AiMenuSection;
  readonly family?: AiMenuFamily;
  readonly starter?: number;
  readonly situational?: true;
}

const PLACEMENTS: ReadonlyMap<string, AiMenuPlacement> = new Map(
  AI_MENU_CATALOG.sections.flatMap((s) =>
    s.items.flatMap((item): [string, AiMenuPlacement][] =>
      "family" in item
        ? item.family.kinds.map((k) => [k.page, { section: s.id, family: item.family }])
        : [[item.page, { section: s.id, ...(item.starter === undefined ? {} : { starter: item.starter }), ...(item.situational === true ? { situational: true as const } : {}) }]],
    ),
  ),
);

const HIDDEN: ReadonlySet<string> = new Set(AI_MENU_CATALOG.hidden.map((h) => h.page));

const FAMILIES: ReadonlyMap<string, AiMenuFamily> = new Map(
  AI_MENU_CATALOG.sections.flatMap((s) => s.items.flatMap((item) => ("family" in item ? [[item.family.id, item.family] as const] : []))),
);

/** Trang này nằm ở đâu trong menu — `undefined` = không có nút menu (trang ngoài catalog, hoặc ẩn). */
export function aiMenuPlacementOf(pageId: string): AiMenuPlacement | undefined {
  return PLACEMENTS.get(pageId);
}

/** Q6 — trang chat CỐ Ý không có nút menu. */
export function isAiMenuHiddenPage(pageId: string): boolean {
  return HIDDEN.has(pageId);
}

/** Q4 — trang chỉ vào lượt menu khi `ready()` của nó ≠ `not_ready` (tầng api kiểm, cổng quyền trước). Tập ĐÓNG suy từ catalog. */
export const AI_MENU_SITUATIONAL_PAGES: readonly string[] = Object.freeze([...PLACEMENTS].filter(([, p]) => p.situational === true).map(([id]) => id));

/** Họ gộp theo id (`payload.family` của nút họ) — `undefined` = họ lạ (chip hỏng/giả ⇒ 422 ở tầng api). */
export function aiMenuFamilyOf(familyId: string): AiMenuFamily | undefined {
  return FAMILIES.get(familyId);
}

/** Số MỤC menu tối đa (dòng trang + dòng họ) — nguồn của trần chip menu (`AI_MENU_MAX_CHIPS`), không ai gõ con số này. */
export const AI_MENU_CATALOG_ENTRY_COUNT = AI_MENU_CATALOG.sections.reduce((n, s) => n + s.items.length, 0);
